import React from "react";
import { useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import { CheckoutStepValidationResult } from "@/redux/modules/api/data/cart/@types/cart.types";
import * as CheckUserModule from "@/redux/modules/api/data/checkUser/checkUser.module";
import { $CheckUserModuleName } from "@/redux/modules/api/data/checkUser/checkUser.types";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import { $CustomerModuleName } from "@/redux/modules/api/data/customer/customer.types";
import { isLoading } from "@/redux/modules/api/loading/loading.selector";
import { update as updateAccountForms } from "@/redux/modules/value/accountForms/accountForms.module";
import { getAccountForms } from "@/redux/modules/value/accountForms/accountForms.selectors";
import { TAccountFormsState } from "@/redux/modules/value/accountForms/accountForms.types";
import { BreakpointProvider } from "@/utils/breakpoints/breakpoints.provider";
import { CheckoutSteps } from "@/utils/enums";

import { useCheckout } from "../useCheckout.hook";
import CheckoutRegisterFormComponent from "./registerCheckoutForm.component";

type Props = {
    title: string;
    buttonText: string;
    notification: string;
    privacyLink: string;
    link: string;
};

const RegisterCheckoutForm = ({
    title,
    buttonText,
    notification,
    privacyLink,
    link: target
}: Props) => {
    const dispatch = useThunkDispatch();
    const accountForms = useSelector(getAccountForms());
    const isCustomerLoading = useSelector(isLoading($CustomerModuleName));
    const isCheckUserLoading = useSelector(isLoading($CheckUserModuleName));

    const { validateCheckoutStep, cartLoading } = useCheckout();

    const load = (email: string) => dispatch(CheckUserModule.load(email));
    const update = (data: TAccountFormsState) =>
        dispatch(updateAccountForms(data));

    const registerGuest = (email: string) =>
        dispatch(CustomerModule.registerGuest(email));

    const createSubmitHandler =
        (formHandler: (e?: React.BaseSyntheticEvent) => Promise<void>) =>
        async (event?: React.BaseSyntheticEvent) => {
            event?.preventDefault();

            const validationResult = await validateCheckoutStep(
                CheckoutSteps.login,
                true
            );

            if (validationResult === CheckoutStepValidationResult.Valid) {
                return formHandler(event);
            }
        };

    const handleOnSubmit = (email: string) =>
        registerGuest(email).then((action) => {
            if (action.type === CustomerModule.APIStatus.SUCCESS) {
                location.assign(target);
            }
        });

    return (
        <BreakpointProvider>
            <CheckoutRegisterFormComponent
                title={title}
                buttonText={buttonText}
                notification={notification}
                privacypolicy={privacyLink}
                isOpen={!!accountForms?.registerComponent}
                updateCardToggle={(isOpen) =>
                    update({
                        loginComponent: false,
                        registerComponent: isOpen
                    })
                }
                onSubmit={handleOnSubmit}
                createSubmitHandler={createSubmitHandler}
                isLoading={
                    isCustomerLoading || isCheckUserLoading || cartLoading
                }
                load={load}
            />
        </BreakpointProvider>
    );
};

export { RegisterCheckoutForm };
